import React, { useState, useRef, useMemo, useEffect } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';

import { useMerchant } from '../../../contexts/MerchantContext';
import { DataTable, Column } from '../../../common/DataTable';
import { Pagination } from '../../../common/DataTable/Pagination';
import { sanitizeNumericString } from '../../Paylinks/SendPaylinkForm/formValues';
import {
  AuditLogEntry,
  AuditLogResource,
  GetAuditLogSortQuery,
  GetAuditLogsQueryFilters,
} from '../../../store/modules/merchant/types';
import { useGetAuditLogsQuery } from '../../../store/modules/merchant/api';
import { AuditLogActionChip } from './AuditLogActionChip';
import { AuditLogDetails } from './AuditLogDetails';
import { AuditLogFiltersPanel } from './AuditLogFiltersPanel';
import { AuditLogFiltersFormValues } from '../../../hooks/useAuditLogFiltersForm';
import { FiltersButton } from '../../Paylinks/components';
import { RefreshButton } from '../../Paylinks/components/RefreshButton';
import { getAuditLogResourceLabel } from '../resourceLabels';

const columns: Column<AuditLogEntry>[] = [
  {
    id: 'action',
    headerName: 'Action',
    renderCell: (row) => (
      <AuditLogActionChip action={row.action} resource={row.resource} size="small" />
    ),
    maxWidth: 140,
    minWidth: 140,
  },
  {
    id: 'resource',
    headerName: 'Resource',
    valueGetter: (row) => getAuditLogResourceLabel(row.resource),
    maxWidth: 150,
    minWidth: 150,
  },
  {
    headerName: 'Date',
    id: 'timestamp_date',
    valueGetter: (row) => dayjs(row.timestamp).format('MM/DD/YY'),
    maxWidth: 100,
    minWidth: 100,
    sortable: true,
    sortName: 'timestamp',
  },
  {
    headerName: 'Time',
    id: 'timestamp_time',
    valueGetter: (row) => dayjs(row.timestamp).format('hh:mm:ss A'),
    maxWidth: 120,
    minWidth: 120,
  },
  {
    headerName: 'Performed by',
    id: 'user',
    valueGetter: (row) => row.user,
    minWidth: 180,
  },
  {
    id: 'details',
    headerName: 'Details',
    renderCell: (row) => <AuditLogDetails auditLog={row} />,
    maxWidth: 80,
    minWidth: 80,
    align: 'center',
  },
];

const filterKeys: Array<keyof GetAuditLogsQueryFilters> = [
  'resources',
  'createdBefore',
  'createdAfter',
];

const convertToPanelProps = (
  initialFilterState: Record<string, string>,
): Partial<AuditLogFiltersFormValues> => {
  const initialState: Partial<AuditLogFiltersFormValues> = {};

  if (initialFilterState.resources) {
    initialState.resources = initialFilterState.resources.split(',') as AuditLogResource[];
  }

  if (initialFilterState.createdBefore) {
    initialState.createdBefore = dayjs(initialFilterState.createdBefore);
  }

  if (initialFilterState.createdAfter) {
    initialState.createdAfter = dayjs(initialFilterState.createdAfter);
  }

  return initialState;
};

export const AuditLogsTable = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { merchantId } = useMerchant();
  const [pageNumber, setPageNumber] = useState(
    Number(sanitizeNumericString(searchParams.get('pageNumber'))) || 1,
  );
  const [pageSize, setPageSize] = useState(
    Number(sanitizeNumericString(searchParams.get('pageSize'))) || 10,
  );
  const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false);
  const [sortQuery, setSortQuery] = useState<GetAuditLogSortQuery>({
    orderBy: ['asc', 'desc'].find((order) => order === searchParams.get('orderBy'))
      ? (searchParams.get('orderBy') as GetAuditLogSortQuery['orderBy'])
      : 'desc',
  });

  const initialFilterState: Record<string, string> = {};
  filterKeys.forEach((key) => {
    if (key === 'resources') {
      const resources = searchParams.getAll('resources');
      if (resources.length > 0) {
        initialFilterState[key] = resources.join(',');
      }
      return;
    }
    if (searchParams.get(key)) {
      initialFilterState[key] = searchParams.get(key) as any;
    }
  });

  const [filters, setFilters] = useState<GetAuditLogsQueryFilters>(initialFilterState);

  const queryParams = useMemo(() => {
    return {
      pageNumber,
      pageSize,
      ...filters,
      ...sortQuery,
    };
  }, [pageNumber, pageSize, filters, sortQuery]);

  const {
    data: response,
    isFetching,
    refetch: refetchAuditLogs,
  } = useGetAuditLogsQuery({ merchantId, ...queryParams }, { skip: !merchantId });

  const filtersCount = useMemo(() => {
    let count = 0;
    if (filters.resources?.length) count += 1;
    if (filters.createdAfter) count += 1;
    if (filters.createdBefore) count += 1;
    return count;
  }, [filters]);

  useEffect(() => {
    // remove any filter keys that are no longer present in queryParams
    filterKeys.forEach((filterKey) => {
      if (!(filterKey in queryParams)) {
        searchParams.delete(filterKey);
      }
    });

    Object.entries(queryParams).forEach(([key, value]) => {
      if (!value) {
        searchParams.delete(key);
        return;
      }
      if (key === 'resources' && Array.isArray(value)) {
        searchParams.delete(key);
        value.forEach((resource) => {
          searchParams.append(key, resource);
        });
        return;
      }
      searchParams.set(key, value as string);
    });

    setSearchParams(searchParams, { replace: true });
  }, [queryParams, searchParams, setSearchParams]);

  const tableRef = useRef<HTMLDivElement>(null);

  return (
    <>
      <Stack gap={2}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <Stack direction="row" gap={2} sx={{ alignItems: 'center' }}>
            <Typography component="h2" fontWeight="semibold" variant="xl">
              View all Audit Logs
            </Typography>
            <RefreshButton onClick={refetchAuditLogs} disabled={isFetching} />
          </Stack>
          <FiltersButton filtersCount={filtersCount} onClick={() => setIsFiltersPanelOpen(true)} />
        </Box>
        <DataTable<AuditLogEntry>
          rows={response?.data ?? []}
          columns={columns}
          getRowId={(row) => row.auditLogId}
          isLoading={isFetching}
          pageSize={pageSize}
          sort={sortQuery}
          setSort={(query) => setSortQuery(query as GetAuditLogSortQuery)}
          tableHeight="calc(100vh - 315px)"
          noRowsMessage="No audit logs found. Try changing the filters."
          tableRef={tableRef}
          renderPagination={() => (
            <Pagination
              itemCount={response?.data.length ?? 0}
              itemTotal={response?.metadata.totalRecords ?? 0}
              pageCount={response?.metadata.totalPages ?? 0}
              onPageChange={(_, p) => {
                setPageNumber(p);
                tableRef.current?.scrollTo({
                  top: 0,
                  behavior: 'smooth',
                });
              }}
              page={pageNumber}
              pageSize={pageSize}
              onPageSizeChange={(newPageSize) => {
                setPageNumber(1);
                setPageSize(newPageSize);
              }}
              disabled={isFetching}
            />
          )}
        />
      </Stack>
      <AuditLogFiltersPanel
        isOpen={isFiltersPanelOpen}
        onClose={() => setIsFiltersPanelOpen(false)}
        initialFilterState={convertToPanelProps(initialFilterState)}
        onFiltersApply={(newFilters) => {
          setPageNumber(1);
          setFilters(newFilters);
        }}
      />
    </>
  );
};
